import { ValueFormatterParams } from 'ag-grid-community';
import {
  CELL_RENDERER,
  ENTITY_TYPES,
  IBaseFormService,
  IXVIListColumn,
} from '../../types';
import { generateDecimalRegexp } from '../../constants';
import { MAX_NUMBER_FOR_STAND_SHEET } from '../../../../config';
import { COLUMN_ALIGN_RIGHT } from '@shared/constants/column-align-definition';
import { DEFAULT_DISPLAY_DECIMAL } from '@config/global-constants';
import { XVIHeaderIconRendererComponent } from '@components/xvi-grid/custom-components/header-icon-renderer/header-icon-renderer.component';
import { STAND_WORKSHEET_HEADER_INFO_MESSAGE } from './stand-worksheet-info-message';
import { get, isNil } from 'lodash';
import { IStandSheetCombineData } from '@shared/types/entities/stand-worksheet';
import { getPOSName } from '@shared/utils/theme.utils';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';

export const informationDataParamsCellRender = (
  isDisabled: boolean,
): Object => {
  return {
    disabled: isDisabled,
    placeholder: DEFAULT_DISPLAY_DECIMAL,
    disableKeyUpFinishEditing: true,
  };
};

const standSheetDataParamsCellRender = (
  min: number | null,
  max: number | null,
  placeholder: string = DEFAULT_DISPLAY_DECIMAL,
): Object => {
  return {
    min,
    max,
    placeholder,
    defaultValue: null,
    disableKeyUpFinishEditing: true,
  };
};

const validDecimalCellParams = standSheetDataParamsCellRender(
  0,
  MAX_NUMBER_FOR_STAND_SHEET,
);

export const getStandSheetPinnedColumn = (
  formService: IBaseFormService<ENTITY_TYPES.STAND_WORKSHEET>,
): IXVIListColumn[] => [
  {
    path: 'chargeable',
    title: '',
    defaultView: true,
    pinned: 'left',
    lockPinned: true,
    lockPosition: true,
    minWidth: 40,
    maxWidth: 40,
    sortable: false,
    valueFormatter: (params): string => {
      return params.value ? '$' : '';
    },
    headerComponentParams: getTipConfig('chargeable', {
      i18nServiced: formService.i18nService,
    }),
  },
  {
    path: 'name',
    title: I18n.Forms.GridForm.ItemName,
    cellClass: 'cell-wrap-text',
    defaultView: true,
    pinned: 'left',
    lockPinned: true,
    lockPosition: true,
    width: 250,
    sortable: false,
    headerComponentFramework: XVIHeaderIconRendererComponent,
    autoHeight: true,
    headerComponentParams: {
      displayName: formService.i18nService.instant(
        I18n.Forms.GridForm.ItemName,
      ),
      headerIconClick: formService.updateEntryFormRowHeight.bind(formService),
    },
    filterParams: {
      quickFilter: true,
    },
  },
  {
    path: 'code',
    title: I18n.Forms.GridForm.ItemID,
    defaultView: true,
    pinned: 'left',
    lockPinned: true,
    lockPosition: true,
    width: 100,
    sortable: false,
    filterParams: {
      quickFilter: true,
    },
  },
  {
    path: 'parent_product_name',
    title: I18n.Forms.GridForm.ProductName,
    defaultView: true,
    pinned: 'left',
    lockPinned: true,
    lockPosition: true,
    width: 100,
    sortable: false,
    filterParams: {
      quickFilter: true,
    },
  },
  {
    path: 'parent_product_id',
    title: I18n.Forms.GridForm.ProductID,
    defaultView: true,
    pinned: 'left',
    lockPinned: true,
    lockPosition: true,
    width: 100,
    sortable: false,
    filterParams: {
      quickFilter: true,
    },
  },
  {
    path: 'parent_product_price',
    title: I18n.Forms.GridForm.UnitPrice,
    defaultView: true,
    pinned: 'left',
    lockPinned: true,
    lockPosition: true,
    width: 100,
    sortable: false,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    headerComponentParams: getTipConfig('parent_product_price', {
      i18nServiced: formService.i18nService,
    }),
    ...COLUMN_ALIGN_RIGHT,
  },
];

const getTipConfig = (
  field: string,
  options: { i18nServiced: XVII18nService },
): any => ({
  hintOptions: {
    description: options.i18nServiced.instant(
      STAND_WORKSHEET_HEADER_INFO_MESSAGE[field],
      {
        pos: getPOSName({ i18nService: options.i18nServiced }),
      },
    ),
    placement: 'top',
  },
});

const getViewModeColumnConfig = (
  formService: IBaseFormService<ENTITY_TYPES.STAND_WORKSHEET>,
  field: string,
  isCurrency: boolean = false,
): Partial<IXVIListColumn> => {
  return {
    cellRenderer: CELL_RENDERER.specific_data_renderer,
    cellRendererParams: {
      isBusy: formService.isIntervalFetching$,
      valueMapper(_, rowData: IStandSheetCombineData): string | number {
        const value = get(rowData, field);
        if (isNil(value)) return null;

        return formService.numberFormatService.getNumberFormatter(isCurrency)({
          value: get(rowData, field),
        });
      },
    },
  };
};

export const getStandSheetUnpinnedColumn = (
  formService: IBaseFormService<ENTITY_TYPES.STAND_WORKSHEET>,
): IXVIListColumn[] => [
  {
    path: 'begin',
    title: I18n.Forms.StandWorksheetForm.BeginCnt,
    defaultView: true,
    headerComponentParams: getTipConfig('begin', {
      i18nServiced: formService.i18nService,
    }),
    width: 70,
    sortable: false,
    valueFormatter: formService.numberFormatService.getNumberFormatter(false),
    ...COLUMN_ALIGN_RIGHT,
    hide: !formService.standWorksheetService.hasBeginCountPermission(),
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'pre_event_in',
    title: I18n.Forms.StandWorksheetForm.PreIn,
    defaultView: true,
    headerComponentParams: getTipConfig('pre_event_in', {
      i18nServiced: formService.i18nService,
    }),
    width: 70,
    sortable: false,
    valueFormatter: formService.numberFormatService.getNumberFormatter(false),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
    cellRenderer: CELL_RENDERER.modal_link_render,
    cellRendererParams: {
      showModalDialogHandler:
        formService.showTransferModalDialogHandler.bind(formService),
      isStopPropagation: true,
    },
  },
  {
    path: 'manager_begin_view',
    labelPath: 'manager_begin',
    title: I18n.Forms.StandWorksheetForm.MgrOvrd,
    defaultView: true,
    headerComponentParams: getTipConfig('manager_begin', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    sortable: false,
    ...getViewModeColumnConfig(formService, 'manager_begin'),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'manager_begin_edit',
    labelPath: 'manager_begin',
    title: I18n.Forms.StandWorksheetForm.MgrOvrd,
    defaultView: true,
    headerComponentParams: getTipConfig('manager_begin', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    sortable: false,
    editable: true,
    valueFormatter: (params): string => {
      return formService.uniformFormattingCellValue(params, false);
    },
    cellRendererParams: validDecimalCellParams,
    cellRendererSelector:
      formService.uniformCellRendererSelector.bind(formService),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'total_available',
    title: I18n.Forms.StandWorksheetForm.TotalAvail,
    defaultView: true,
    headerComponentParams: getTipConfig('total_available', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    sortable: false,
    valueFormatter: formService.numberFormatService.getNumberFormatter(false),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'during_event_in',
    title: I18n.Forms.StandWorksheetForm.DuringIn,
    defaultView: true,
    headerComponentParams: getTipConfig('during_event_in', {
      i18nServiced: formService.i18nService,
    }),
    width: 70,
    sortable: false,
    valueFormatter: formService.numberFormatService.getNumberFormatter(false),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
    cellRenderer: CELL_RENDERER.modal_link_render,
    cellRendererParams: {
      showModalDialogHandler:
        formService.showTransferModalDialogHandler.bind(formService),
      isStopPropagation: true,
    },
  },
  {
    path: 'during_event_out',
    title: I18n.Forms.StandWorksheetForm.DuringOut,
    defaultView: true,
    headerComponentParams: getTipConfig('during_event_out', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    sortable: false,
    valueFormatter: formService.numberFormatService.getNumberFormatter(false),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
    cellRenderer: CELL_RENDERER.modal_link_render,
    cellRendererParams: {
      showModalDialogHandler:
        formService.showTransferModalDialogHandler.bind(formService),
      isStopPropagation: true,
    },
  },
  {
    path: 'gratis_view',
    labelPath: 'gratis',
    title: I18n.Forms.StandWorksheetForm.Gratis,
    defaultView: true,
    headerComponentParams: getTipConfig('gratis', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    sortable: false,
    ...getViewModeColumnConfig(formService, 'gratis'),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'gratis_edit',
    labelPath: 'gratis',
    title: I18n.Forms.StandWorksheetForm.Gratis,
    defaultView: true,
    headerComponentParams: getTipConfig('gratis', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    sortable: false,
    editable: true,
    valueFormatter: formService.numberFormatService.getNumberFormatter(false),
    cellRendererSelector:
      formService.uniformCellRendererSelector.bind(formService),
    cellRendererParams: validDecimalCellParams,
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'ingredient_spoil_view',
    labelPath: 'ingredient_spoil',
    title: I18n.Forms.StandWorksheetForm.Spoilage,
    defaultView: true,
    headerComponentParams: getTipConfig('ingredient_spoil', {
      i18nServiced: formService.i18nService,
    }),
    sortable: false,
    width: 120,
    ...getViewModeColumnConfig(formService, 'ingredient_spoil'),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'ingredient_spoil_edit',
    labelPath: 'ingredient_spoil',
    title: I18n.Forms.StandWorksheetForm.Spoilage,
    defaultView: true,
    headerComponentParams: getTipConfig('ingredient_spoil', {
      i18nServiced: formService.i18nService,
    }),
    sortable: false,
    editable: true,
    width: 120,
    valueFormatter: formService.numberFormatService.getNumberFormatter(false),
    cellRendererSelector:
      formService.uniformCellRendererSelector.bind(formService),
    cellRendererParams: validDecimalCellParams,
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'adjustment_view',
    labelPath: 'adjustment',
    title: I18n.Forms.StandWorksheetForm.Adj,
    defaultView: true,
    headerComponentParams: getTipConfig('adjustment', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    sortable: false,
    valueFormatter: formService.numberFormatService.getNumberFormatter(false),
    ...COLUMN_ALIGN_RIGHT,
    ...getViewModeColumnConfig(formService, 'adjustment'),
    hide: !formService.standWorksheetService.hasAdjustmentPermission(),
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'adjustment_edit',
    labelPath: 'adjustment',
    title: I18n.Forms.StandWorksheetForm.Adj,
    defaultView: true,
    headerComponentParams: getTipConfig('adjustment', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    sortable: false,
    editable: true,
    valueFormatter: formService.numberFormatService.getNumberFormatter(false),
    cellRendererSelector:
      formService.uniformCellRendererSelector.bind(formService),
    cellRendererParams: {
      acceptableInputRegex: generateDecimalRegexp(
        formService.stateService.getNumberPrecision(),
        false,
      ),
      ...standSheetDataParamsCellRender(
        -MAX_NUMBER_FOR_STAND_SHEET,
        MAX_NUMBER_FOR_STAND_SHEET,
      ),
    },
    ...COLUMN_ALIGN_RIGHT,
    hide: !formService.standWorksheetService.hasAdjustmentPermission(),
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'expected_qty',
    title: I18n.Forms.StandWorksheetForm.ExpQty,
    defaultView: true,
    headerComponentParams: getTipConfig('expected_qty', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    sortable: false,
    valueFormatter: formService.numberFormatService.getNumberFormatter(false),
    ...COLUMN_ALIGN_RIGHT,
    hide: !formService.standWorksheetService.hasExpectedQTYPermission(),
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'end_view',
    labelPath: 'end',
    title: I18n.Forms.StandWorksheetForm.EndCnt,
    defaultView: true,
    headerComponentParams: getTipConfig('end', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    sortable: false,
    ...getViewModeColumnConfig(formService, 'end'),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'end_edit',
    labelPath: 'end',
    title: I18n.Forms.StandWorksheetForm.EndCnt,
    defaultView: true,
    headerComponentParams: getTipConfig('end', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    sortable: false,
    editable: true,
    valueFormatter: (params): string => {
      return formService.uniformFormattingCellValue(params, false);
    },
    cellRendererSelector:
      formService.uniformCellRendererSelector.bind(formService),
    cellRendererParams: validDecimalCellParams,
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'sale',
    title: I18n.Forms.StandWorksheetForm.TermQty,
    defaultView: true,
    headerComponentParams: getTipConfig('sale', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    sortable: false,
    ...getViewModeColumnConfig(formService, 'sale'),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'price',
    title: `${formService.i18nService.instant(
      I18n.Forms.StandWorksheetForm.Term,
    )}$`,
    defaultView: true,
    headerComponentParams: getTipConfig('price', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    sortable: false,
    ...getViewModeColumnConfig(formService, 'price', true),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'physical_qty',
    title: I18n.Forms.StandWorksheetForm.PhysQty,
    defaultView: true,
    headerComponentParams: getTipConfig('physical_qty', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    sortable: false,
    valueFormatter: formService.numberFormatService.getNumberFormatter(false),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'physical_dollars',
    title: `${formService.i18nService.instant(
      I18n.Forms.StandWorksheetForm.Phys,
    )}$`,
    defaultView: true,
    headerComponentParams: getTipConfig('physical_dollars', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    sortable: false,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'price_adjustment',
    title: I18n.Forms.StandWorksheetForm.PriceAdj,
    defaultView: true,
    headerComponentParams: getTipConfig('price_adjustment', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    sortable: false,
    ...getViewModeColumnConfig(formService, 'price_adjustment', true),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'tax',
    title: I18n.Forms.StandWorksheetForm.Taxes,
    defaultView: true,
    headerComponentParams: getTipConfig('tax', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    sortable: false,
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
    ...getViewModeColumnConfig(formService, 'tax', true),
  },
  {
    path: 'discount',
    title: I18n.Forms.StandWorksheetForm.Disc,
    defaultView: true,
    headerComponentParams: getTipConfig('discount', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    sortable: false,
    ...getViewModeColumnConfig(formService, 'discount', true),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'sale_post_closure',
    title: I18n.Forms.StandWorksheetForm.PostClsQty,
    defaultView: true,
    headerComponentParams: getTipConfig('sale_post_closure', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    sortable: false,
    valueFormatter: formService.numberFormatService.getNumberFormatter(false),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'price_post_closure',
    title: `${formService.i18nService.instant(
      I18n.Forms.StandWorksheetForm.PostCls,
    )}$`,
    defaultView: true,
    headerComponentParams: getTipConfig('price_post_closure', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    sortable: false,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'tax_post_closure',
    title: I18n.Forms.StandWorksheetForm.PostClsTaxes,
    defaultView: true,
    headerComponentParams: getTipConfig('tax_post_closure', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    sortable: false,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'discount_post_closure',
    title: I18n.Forms.StandWorksheetForm.PostClsDisc,
    defaultView: true,
    headerComponentParams: getTipConfig('discount_post_closure', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    sortable: false,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'variance_qty',
    title: I18n.Forms.StandWorksheetForm.VarQty,
    defaultView: true,
    headerComponentParams: getTipConfig('variance_qty', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    sortable: false,
    valueFormatter: formService.numberFormatService.getNumberFormatter(false),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
  {
    path: 'variance_dollars',
    title: `${formService.i18nService.instant(
      I18n.Forms.StandWorksheetForm.Var,
    )}$`,
    defaultView: true,
    headerComponentParams: getTipConfig('variance_dollars', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    sortable: false,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    ...COLUMN_ALIGN_RIGHT,
    headerClass: 'entry-form-text-wrapping',
  },
];

export const getInformationTabledColumn = (
  formService: IBaseFormService<ENTITY_TYPES.STAND_WORKSHEET>,
): IXVIListColumn[] => [
  {
    path: 'physical_sales',
    title: I18n.Forms.StandWorksheetForm.PhysicalSales,
    defaultView: true,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    cellRendererParams: informationDataParamsCellRender(true),
    width: 120,
    headerComponentParams: getTipConfig('physical_sales', {
      i18nServiced: formService.i18nService,
    }),
    ...COLUMN_ALIGN_RIGHT,
  },
  {
    path: 'terminal_sales',
    title: I18n.Forms.StandWorksheetForm.TerminalSales,
    defaultView: true,
    headerComponentParams: getTipConfig('terminal_sales', {
      i18nServiced: formService.i18nService,
    }),
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    cellRendererParams: informationDataParamsCellRender(true),
    width: 120,
    ...COLUMN_ALIGN_RIGHT,
  },
  {
    path: 'phys_terminal_diff',
    title: I18n.Forms.StandWorksheetForm.PhysTerminalDiff,
    defaultView: true,
    headerComponentParams: getTipConfig('phys_terminal_diff', {
      i18nServiced: formService.i18nService,
    }),
    cellRenderer: CELL_RENDERER.custom_text_renderer,
    valueFormatter: (params: ValueFormatterParams): string => {
      return formService.standWorksheetService.showDiffNumberAndPercentage(
        params.data,
        'phys_terminal_diff',
        'physical_sales',
      );
    },
    cellClassRules: formService.getInformationGridRowCellClass(
      'phys_terminal_diff',
      'physical_sales',
      'PHYSICAL_TERMINAL',
    ),
    cellRendererParams: formService.getInformationGridRowCellRenderParams(
      'phys_terminal_diff',
      'physical_sales',
      'PHYSICAL_TERMINAL',
      informationDataParamsCellRender(true),
    ),
    width: 170,
    headerClass: 'ag-right-aligned-header',
  },
  {
    path: 'total_tax',
    title: I18n.Forms.StandWorksheetForm.TotalTaxes,
    defaultView: true,
    headerComponentParams: getTipConfig('total_tax', {
      i18nServiced: formService.i18nService,
    }),
    width: 115,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    cellRendererParams: informationDataParamsCellRender(true),
    ...COLUMN_ALIGN_RIGHT,
  },
  {
    path: 'cash_count',
    title: I18n.Forms.StandWorksheetForm.CashCount,
    defaultView: true,
    headerComponentParams: getTipConfig('cash_count', {
      i18nServiced: formService.i18nService,
    }),
    editable: true,
    width: 115,
    cellRenderer: CELL_RENDERER.price_input_renderer,
    cellRendererParams: (): Object => {
      return {
        disabled: formService.disableFieldWhenIsFinal(),
        placeholder: DEFAULT_DISPLAY_DECIMAL,
        disableKeyUpFinishEditing: true,
        acceptableInputRegex: generateDecimalRegexp(
          formService.stateService.getCurrencyPrecision(),
        ),
        min: 0,
        max: MAX_NUMBER_FOR_STAND_SHEET,
        usePreferenceDecimalPrecision: {
          overrideMax: true,
          overrideMin: false,
        },
      };
    },
    ...COLUMN_ALIGN_RIGHT,
  },
  {
    path: 'receipt_physical_diff',
    title: I18n.Forms.StandWorksheetForm.ReceiptPhysicalDiff,
    defaultView: true,
    headerComponentParams: getTipConfig('receipt_physical_diff', {
      i18nServiced: formService.i18nService,
    }),
    width: 170,
    cellRenderer: CELL_RENDERER.custom_text_renderer,
    valueFormatter: (params: ValueFormatterParams): string => {
      return formService.standWorksheetService.showDiffNumberAndPercentage(
        params.data,
        'receipt_physical_diff',
        'receipt_total',
      );
    },
    cellClassRules: formService.getInformationGridRowCellClass(
      'receipt_physical_diff',
      'receipt_total',
      'RECEIPT_PHYSICAL',
    ),
    cellRendererParams: formService.getInformationGridRowCellRenderParams(
      'receipt_physical_diff',
      'receipt_total',
      'RECEIPT_PHYSICAL',
      informationDataParamsCellRender(true),
    ),
    headerClass: 'ag-right-aligned-header',
  },
  {
    path: 'credit_debit_gift_total',
    title: I18n.Forms.StandWorksheetForm.CreditGiftCards,
    defaultView: true,
    headerComponentParams: getTipConfig('credit_debit_gift_total', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    cellRendererParams: informationDataParamsCellRender(true),
    ...COLUMN_ALIGN_RIGHT,
  },
  {
    path: 'cash',
    title: I18n.Forms.StandWorksheetForm.TerminalCash,
    defaultView: true,
    headerComponentParams: getTipConfig('cash', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    cellRendererParams: informationDataParamsCellRender(true),
    ...COLUMN_ALIGN_RIGHT,
  },
  {
    path: 'custom',
    title: I18n.Forms.StandWorksheetForm.CustomTenders,
    defaultView: true,
    headerComponentParams: getTipConfig('custom_tenders', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    cellRendererParams: informationDataParamsCellRender(true),
    ...COLUMN_ALIGN_RIGHT,
  },
  {
    path: 'tips',
    title: I18n.Forms.StandWorksheetForm.Tips,
    defaultView: true,
    headerComponentParams: getTipConfig('tips', {
      i18nServiced: formService.i18nService,
    }),
    width: 90,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    cellRendererParams: informationDataParamsCellRender(true),
    ...COLUMN_ALIGN_RIGHT,
  },
  {
    path: 'total_dollars_discounted',
    title: I18n.Forms.StandWorksheetForm.TotalDiscounts,
    defaultView: true,
    headerComponentParams: getTipConfig('total_dollars_discounted', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    cellRendererParams: informationDataParamsCellRender(true),
    ...COLUMN_ALIGN_RIGHT,
  },
  {
    path: 'miscellaneous',
    title: I18n.Forms.StandWorksheetForm.FeesOther,
    defaultView: true,
    headerComponentParams: getTipConfig('miscellaneous', {
      i18nServiced: formService.i18nService,
    }),
    width: 115,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    cellRendererParams: informationDataParamsCellRender(true),
    ...COLUMN_ALIGN_RIGHT,
  },
  {
    path: 'post_closure_sales',
    title: I18n.Forms.StandWorksheetForm.PostClosureSales,
    defaultView: true,
    headerComponentParams: getTipConfig('post_closure_sales', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    cellRendererParams: informationDataParamsCellRender(true),
    ...COLUMN_ALIGN_RIGHT,
  },
  {
    path: 'total_tax_post_closure',
    title: I18n.Forms.StandWorksheetForm.TotalPostClosureTaxes,
    defaultView: true,
    headerComponentParams: getTipConfig('total_tax_post_closure', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    cellRendererParams: informationDataParamsCellRender(true),
    ...COLUMN_ALIGN_RIGHT,
  },
  {
    path: 'credit_debit_gift_post_closure_total',
    title: I18n.Forms.StandWorksheetForm.PostClosureGiftCards,
    defaultView: true,
    headerComponentParams: getTipConfig(
      'credit_debit_gift_post_closure_total',
      {
        i18nServiced: formService.i18nService,
      },
    ),
    width: 120,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    cellRendererParams: informationDataParamsCellRender(true),
    ...COLUMN_ALIGN_RIGHT,
  },
  {
    path: 'custom_post_closure',
    title: I18n.Forms.StandWorksheetForm.PostClosureCustom,
    defaultView: true,
    headerComponentParams: getTipConfig('custom_post_closure', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    cellRendererParams: informationDataParamsCellRender(true),
    ...COLUMN_ALIGN_RIGHT,
  },
  {
    path: 'tips_post_closure',
    title: I18n.Forms.StandWorksheetForm.PostClosureTips,
    defaultView: true,
    headerComponentParams: getTipConfig('tips_post_closure', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    cellRendererParams: informationDataParamsCellRender(true),
    ...COLUMN_ALIGN_RIGHT,
  },
  {
    path: 'cash_post_closure',
    title: I18n.Forms.StandWorksheetForm.PostClosureTerminalCash,
    defaultView: true,
    headerComponentParams: getTipConfig('cash_post_closure', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    cellRendererParams: informationDataParamsCellRender(true),
    ...COLUMN_ALIGN_RIGHT,
  },
  {
    path: 'post_closure_dollars_discounted',
    title: I18n.Forms.StandWorksheetForm.TotalPostClosureDiscounts,
    defaultView: true,
    headerComponentParams: getTipConfig('post_closure_dollars_discounted', {
      i18nServiced: formService.i18nService,
    }),
    width: 120,
    valueFormatter: formService.numberFormatService.getNumberFormatter(true),
    cellRendererParams: informationDataParamsCellRender(true),
    ...COLUMN_ALIGN_RIGHT,
  },
];
